import { css } from 'styled-components'
import type { AnimationGeneratorType, Easing } from 'motion/react'

export const breakpointsValues = {
  'watch': 180,
  'mobile-xs': 320,
  'iphone6': 375,
  'mobile': 414,
  'nexus7': 603,
  'tablet': 768,
  'desktop-xs': 992,
  'desktop-md': 1200,
  'ipadpro-12': 1284,
  'desktop': 1440,
  'desktop-wide': 1650,
  'container': 1800,
  'desktop-large': 1920,
  'desktop-wqhd': 2560, /* 2k */
  'desktop-uhd': 3840,  /* 4k */
  'desktop-ultrawide': 5120
} as const

export const getBreakpointPx = (key: TypeBreakpointKey) => `${breakpointsValues[key]}px`

export type TypeBreakpointKey = keyof typeof breakpointsValues
type TypeMq = {
  greaterThan: (device:TypeBreakpointKey) => string
  lessThan: (device:TypeBreakpointKey) => string
  between: (fromDevice: TypeBreakpointKey, untilDevice: TypeBreakpointKey) => string
}

function pxToEm(breakpoint: TypeBreakpointKey, baseRemPx = 16): string {
  const breakpointValue = getBreakpointPx(breakpoint)
  if (!breakpointValue) throw new Error(`Breakpoint "${breakpoint}" not found.`)

  const value = breakpointsValues[breakpoint]
  const emValue = value / baseRemPx // Assuming 1rem = 16px

  return `${emValue}em`
}

export const mq: TypeMq = {
  greaterThan: (device) => `@media (min-width: ${pxToEm(device)})`,
  lessThan: (device) => `@media (max-width: ${pxToEm(device)})`,
  between: (fromDevice, untilDevice) => `@media (min-width: ${pxToEm(fromDevice)}) and (max-width: ${pxToEm(untilDevice)})`
}

export const vw = (size: number, breakpoint: TypeBreakpointKey | number, unit = 'vw') => {
  const screenWidth = typeof breakpoint === 'string' ? getBreakpointPx(breakpoint) : `${breakpoint}px`
  return `${(100 * size / parseInt(screenWidth))}${unit}`
}

export const vh = (size: number, breakpoint: TypeBreakpointKey | number) => {
  const screenWidth = typeof breakpoint === 'string' ? getBreakpointPx(breakpoint) : `${breakpoint}px`
  return `${(100 * size / parseInt(screenWidth))}vh`
}

type GetRelativePixelParams = {
  startSize: number
  startMq: TypeBreakpointKey | number
  endMq: TypeBreakpointKey | number
  precision?: number
}
const getBreakpointNumber = (bp: TypeBreakpointKey | number) =>
  typeof bp === 'number' ? bp : breakpointsValues[bp]

export const getRelativePixel = ({ startSize, startMq, endMq, precision = 2 }: GetRelativePixelParams) => {
  const startWidth = getBreakpointNumber(startMq)
  const endWidth = getBreakpointNumber(endMq)

  if (!Number.isFinite(startSize) || startSize <= 0) return 0
  if (!Number.isFinite(startWidth) || startWidth <= 0) return 0
  if (!Number.isFinite(endWidth) || endWidth <= 0) return 0

  const raw = startSize * (endWidth / startWidth)

  const factor = 10 ** precision
  return Math.round(raw * factor) / factor
}

export const getEasing = (duration = 300) => (
  {
    duration,
    ease: [.25, .1, .25, 1]
  }
)

export const getTransition = (duration = .3) => ({
  duration,
  type: 'tween' as AnimationGeneratorType,
  ease: [.25, .1, .25, 1] as Easing
}) as const

export type Col = 'auto' | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
interface iColumns {
  number: 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
  width: number
}

interface iDeviceGrid {
  maxWidth: number
  gutter: { width: number }
  columns: iColumns
}

export interface iGrid {
  readonly mobile?: iDeviceGrid
  readonly tablet?: iDeviceGrid
  readonly desktop?: iDeviceGrid
}

export const defaultGrid: iGrid = {
  mobile: {
    maxWidth: 390, // mobile
    gutter: { width: 17 },
    columns: {
      number: 6,
      width: 48
    }
  },
  tablet: {
    maxWidth: 768,
    gutter: { width: 12 },
    columns: {
      number: 12,
      width: 50
    }
  },
  desktop: {
    maxWidth: 1440,
    gutter: { width: 50 },
    columns: {
      number: 12,
      width: 70
    }
  }
} as const

export const remainingGridWidth = ({ mobile = defaultGrid.mobile!, tablet = defaultGrid.tablet!, desktop = defaultGrid.desktop! }: iGrid) => {
  return {
    mobile: mobile.maxWidth - ((mobile.columns.width * mobile.columns.number) + (mobile.gutter.width * (mobile.columns.number - 1))),
    tablet: tablet.maxWidth - ((tablet.columns.width * tablet.columns.number) + (tablet.gutter.width * (tablet.columns.number - 1))),
    desktop: desktop.maxWidth - ((desktop.columns.width * desktop.columns.number) + (desktop.gutter.width * (desktop.columns.number - 1))),
  }
}

export const fullWidth = ({ mobile = defaultGrid.mobile!, tablet = defaultGrid.tablet!, desktop = defaultGrid.desktop! }) => {
  return css`
    transform: translateX(-${vw(((mobile.maxWidth + (mobile.gutter.width / 2)) - (mobile.columns.width + mobile.gutter.width) * mobile.columns.number), 'mobile')});
    width: 100vw;

    @media (min-width: 768px) {
      transform: translateX(-${vw(((tablet.maxWidth + (tablet.gutter.width / 4)) - (tablet.columns.width + tablet.gutter.width) * tablet.columns.number), 'tablet')});
    }

    @media (min-width: 1440px) {
      transform: translateX(-${vw(((desktop.maxWidth + (desktop.gutter.width / 8)) - (desktop.columns.width + desktop.gutter.width) * desktop.columns.number), 'desktop')});
    }
  `
}

export const fullWidthMargins = ({ mobile = defaultGrid.mobile!, tablet = defaultGrid.tablet!, desktop = defaultGrid.desktop! }) => {
  return css`
    margin-left: ${vw((mobile.maxWidth - (mobile.columns.width + mobile.gutter.width) * mobile.columns.number), 'mobile')};

    @media (min-width: 768px) {
      margin-left: ${vw((tablet.maxWidth - (tablet.columns.width + tablet.gutter.width) * tablet.columns.number), 'tablet')};
    }

    @media (min-width: 1440px) {
      margin-left: ${vw((desktop.maxWidth - (desktop.columns.width + desktop.gutter.width) * desktop.columns.number), 'desktop')};
    }
  `
}

export const designGrid = ({ mobile = defaultGrid.mobile!, tablet = defaultGrid.tablet!, desktop = defaultGrid.desktop! }: iGrid) => {
  return css`
    display: grid;
    grid-template-columns: repeat(${mobile.columns.number}, ${vw(mobile.columns.width, mobile.maxWidth, '%')});
    grid-column-gap: ${vw(mobile.gutter.width, mobile.maxWidth, '%')};
    grid-row-gap: 0;
    justify-content: center;
    width: 100%;

    @media (min-width: 768px) {
      grid-column-gap: ${vw(tablet.gutter.width, tablet.maxWidth, '%')};
      grid-template-columns: repeat(${tablet.columns.number}, ${vw(tablet.columns.width, tablet.maxWidth, '%')});
    }

    @media (min-width: 1440px) {
      grid-column-gap: ${vw(desktop.gutter.width, desktop.maxWidth, '%')};
      grid-template-columns: repeat(${desktop.columns.number}, ${vw(desktop.columns.width, desktop.maxWidth, '%')});
    }
  `
}

export const innerGrid = (config: iDeviceGrid) => {
  const { gutter, columns, maxWidth } = config

  // Determine the fraction-based grid template columns value
  // Each column width is treated as a fraction of the total grid width
  const gridTemplateColumns = `repeat(${columns.number}, minmax(0, 1fr))`

  // Use the gutter width as the grid gap
  return css`
    display: grid;
    grid-column-gap: ${vw(gutter.width, maxWidth, '%')};
    grid-template-columns: ${gridTemplateColumns};
    width: 100%;
  `
}

// ${innerGrid({
//   maxWidth: 1440,
//   gutter: {
//     width: 20,
//   },
//   columns: {
//     number: 12,
//     width: 97,
//   },
// })}
